import React from 'react';

import clsx, { type ClassValue } from 'clsx';
import { CircleX } from 'lucide-react';
import { useFormContext } from 'react-hook-form';

import {
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { isNullOrEmpty } from '@/services/helper';

type TInputControl = {
	name: string,
	label?: string,
	className?: ClassValue,
	placeholder?: string,
	onChangeListen?: (e: React.ChangeEvent<HTMLInputElement>) => void,
	isDisabled?: boolean,
	hint?: React.ReactNode,
	isClearable?: boolean,
	leftAddOn?: React.ReactNode
} & StrictOmit<React.ComponentProps<'input'>, 'className'>;

const InputControl = ({
	name,
	label,
	type = 'text',
	className,
	placeholder,
	onChangeListen,
	isDisabled = false,
	hint,
	isClearable = false,
	leftAddOn,
	required,
	...props
}: TInputControl) => {
	const { control, resetField, watch } = useFormContext();

	return (
		<FormField
			control={control}
			name={name}
			render={({
				field: { onChange, value, onBlur },
				fieldState: { error },
				formState: { disabled }
			}) => (
				<FormItem className={cn('flex w-full flex-col gap-2', className)}>
					{label ? (
						<FormLabel htmlFor={name}>
							{label} {required ? <span className="text-red">*</span> : null}
						</FormLabel>
					) : null}
					<FormControl>
						<div className="flex w-full flex-col">
							<div className="relative">
								<Input
									{...props}
									onBlur={onBlur}
									id={name}
									className={cn(
										clsx({
											'border border-red-500': !isNullOrEmpty(error)
										}),
										className
									)}
									type={type}
									placeholder={placeholder || label}
									autoComplete="off"
									disabled={isDisabled || disabled}
									value={value || ''}
									onChange={(e) => {
										if (onChangeListen) {
											onChangeListen(e);
										}
										onChange(e);
									}}
								/>
								{leftAddOn}
								{isClearable && watch(name) && type === 'text' && !isDisabled ? (
									<button
										title="Clear"
										type="button"
										onClick={() => resetField(name)}
										className="absolute top-1/2 right-3 -translate-y-1/2"
									>
										<CircleX className="text-slate-500" />
									</button>
								) : null}
							</div>
							<FormMessage className="mt-0" />
							{hint && <FormDescription className="mt-1">{hint}</FormDescription>}
						</div>
					</FormControl>
				</FormItem>
			)}
		/>
	);
};

export default InputControl;
